import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import * as notify from "../shared/notify";
import classnames from "classnames";

import TaskNav from "../components/task-nav/task-nav";
import PaginatedTable from "../components/table";

import { getProductExportUrl, getProducts, updateStatusByIds, getStatusList } from "./lib/stagingData";
import { buildColumns, DEFAULT_SORT, DEFAULT_FILTER } from "./lib/constants";
import { setStorageValue } from "../shared/storage";
import { getCSV } from "shared/data";
import { useNavigate } from "react-router-dom";

const idealAttributesCount = 10;

const PreshipmentIndex = () => {
    const navigate = useNavigate();
    const [selectedUpcs, setSelectedUpcs] = useState([]);
    const [pulse, setPulse] = useState(false);
    const [exportUrl, setExportUrl] = useState("/pre-shipment-packages/export?pagesize=20&pagenumber=1");
    const [totalRecords, setTotalRecords] = useState(0);
    const [workflowStatus, setWorkflowStatus] = useState({
        map: {},
        list: [],
    });

    useEffect(() => {
        async function onInit() {
            const workflowStatuses = await getStatusList();

            if (!workflowStatuses || !workflowStatuses.values) {
                return;
            }

            const newStatusListMap = workflowStatuses.values.reduce((obj, s) => {
                obj[s.value] = s.text;

                return obj;
            }, {});

            setWorkflowStatus({
                map: newStatusListMap,
                list: workflowStatuses.values.filter((s) => [0, 7].includes(s.value)),
            });
        }

        onInit();
    }, []);

    const statusClick = async (statusId) => {
        const upcCount = selectedUpcs.length;
        const statusText = workflowStatus.map[statusId];

        if (!statusText) {
            return;
        }

        if (upcCount <= 0) {
            notify.warning(`You must select at least 1 upc to make ${statusText.toLowerCase()}`);

            return;
        }

        const note = await notify.prompt(`Are you sure you want ${upcCount} record(s) ${statusText.toLowerCase()}?`);

        if (note === false) {
            return;
        }

        setSelectedUpcs([]);

        const response = await updateStatusByIds(selectedUpcs, statusText, note);

        if (!response) {
            return;
        }

        notify.success(`Records ${statusText}`);

        setPulse(!pulse);
    };

    const updateSelectedUpc = (item, isRemove) => {
        if (isRemove === "true") {
            setSelectedUpcs(selectedUpcs.filter((i) => item.id !== i.id));
        } else {
            setSelectedUpcs([...selectedUpcs, { ...item }]);
        }
    };
    const setAllUpcs = (list) => {
        setSelectedUpcs(list);
    };

    const onRowClick = (state, rowInfo, column, instance) => {
        const colorStyle = {};

        if (rowInfo && rowInfo.original.attributesCount && rowInfo.original.attributesCount <= 0) {
            colorStyle.backgroundColor = "rgba(255, 204, 204, 0.5)";
        } else if (rowInfo && rowInfo.original.attributesCount && rowInfo.original.attributesCount <= idealAttributesCount) {
            colorStyle.backgroundColor = "rgba(255, 255, 204, 0.7)";
        }

        const style = {
            cursor: "pointer",
            ...colorStyle,
        };

        return {
            onDoubleClick: (e) => {
                const url = `/preshipment/upc/${rowInfo.original.id}`;
                navigate(url);
            },
            style: style,
        };
    };

    const interceptGetProducts = async (pageNumber, pageSize, sort, filter) => {
        setExportUrl(getProductExportUrl(pageNumber, pageSize, sort, filter, true));

        return getProducts(pageNumber, pageSize, sort, filter, true);
    };

    const updateTotalRecords = (data) => {
        setTotalRecords(data.totalRecords);
    };

    const linkClick = async (e) => {
        if (totalRecords <= 0) {
            e.preventDefault();
        }

        const file = await getCSV(exportUrl);

        if (!file) {
            return notify.error("You do not have permission to perform this action.");
        }

        return file;
    };

    const resetDefault = () => {
        setStorageValue("preshipment-filter", [...DEFAULT_FILTER]);
        setStorageValue("preshipment-sort", [...DEFAULT_SORT]);
        window.location.reload();
    };

    const selectedCount = selectedUpcs.length;
    const idList = selectedUpcs.map((i) => i.id);

    return (
        <div>
            <TaskNav>
                <div className="button-group">
                    <h1>Preshipment</h1>
                    <div className="button-group -right">
                        <button className={classnames("button", { "-disabled": totalRecords <= 0 })} title="Export" onClick={linkClick}>
                            <FontAwesomeIcon icon={faFileExport} />
                        </button>
                        <button
                            className="button -primary"
                            onClick={() => {
                                statusClick(2);
                            }}
                        >
                            Accept ({selectedCount})
                        </button>
                        <button
                            className="button -warning"
                            onClick={() => {
                                statusClick(7);
                            }}
                        >
                            Hold ({selectedCount})
                        </button>
                        <button
                            className="button -error"
                            onClick={() => {
                                statusClick(5);
                            }}
                        >
                            Ignore ({selectedCount})
                        </button>
                    </div>
                </div>
            </TaskNav>
            <div className="button-group -right">
                <button className="button -small -right" onClick={resetDefault}>
                    Reset to Default Sort/Filter
                </button>
            </div>
            <PaginatedTable
                getData={interceptGetProducts}
                getDataCallback={updateTotalRecords}
                onTaskRowClick={onRowClick}
                columns={buildColumns(updateSelectedUpc, setAllUpcs, idList, workflowStatus)}
                storageSortValue="preshipment-sort"
                storageFilterValue="preshipment-filter"
                minRows="10"
                updatePulse={pulse}
            />

            <p className="txt-right">
                <em>Red rows have 0 attributes, yellow rows have less than {idealAttributesCount} attributes</em>
            </p>
        </div>
    );
};

export default PreshipmentIndex;
